import { faPuzzlePiece, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Stack, IconButton } from '@vapor/material'
import { Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { calculateAdditionalServicePrice } from 'src/utils/additional-services-utils'
import { ReservationCartRow } from '../../form/reservation-new.type'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { ChildrenQuery, PricingType } from 'src/api/api.generated'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import { HicChip } from 'src/app/components/hic-chip/hic-chip'
import { additionalServiceShouldHaveQuantity } from '../../form/reservation-new.utils'

type ReservationCartServicesProps = {
  item: ReservationCartRow
  childrenResult: ChildrenQuery | undefined
  isEditable: boolean
  onUpdateRow?: (row: ReservationCartRow | ReservationCartRow[]) => void
}

const ReservationCartServices = (props: ReservationCartServicesProps) => {
  const { item } = props
  const localizeString = useLocalizeString()
  const { formatCurrency } = useFormatCurrency()
  const { t } = useTranslation()

  return (
    <>
      {item.includedAdditionalServices?.map((additionalService) => {
        const serviceGuest =
          (additionalService.automaticForAdults ? item.occupancy.adults : 0) +
          (additionalService.automaticForChildren ? item.occupancy.children.reduce((quantity, child) => quantity + child.quantity, 0) : 0)

        const quantity = additionalServiceShouldHaveQuantity(additionalService.pricingType) ? additionalService.quantity ?? 1 : serviceGuest

        return (
          <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={0.5} key={additionalService.id}>
            <FontAwesomeIcon icon={faPuzzlePiece} color="var(--QuickSilver)" style={{ width: '13px', height: '13px', marginLeft: '4px' }} />
            <Typography variant="caption">
              {`${quantity} x `}
              {localizeString(additionalService.name)}
              {!additionalService.priceZero && (
                <>
                  {' '}
                  {additionalService.pricingType === PricingType.PER_NIGHT || additionalService.pricingType === PricingType.PER_PERSON_PER_NIGHT ? (
                    t('pms.booking.reservation_new.cart.additional-services.nights', { count: additionalService.nights })
                  ) : (
                    <></>
                  )}{' '}
                  {t('pms.booking.reservation_new.cart.tot', {
                    price: formatCurrency(
                      calculateAdditionalServicePrice({
                        adults: item.occupancy.adults,
                        children: item.occupancy.children,
                        price: additionalService.price,
                        days: item.nights,
                        pricingType: additionalService.pricingType,
                        automaticForAdults: additionalService.automaticForAdults,
                        automaticForChildren: additionalService.automaticForChildren,
                        quantity: additionalService.quantity,
                        childPrices: additionalService.childPrices,
                        priceZero: additionalService.priceZero,
                      }),
                      additionalService.currency,
                    ),
                  })}
                </>
              )}
            </Typography>
            <HicChip text={t('pms.booking.reservation_new.cart.additional_service_included')} outlined />
          </Stack>
        )
      })}

      {item.addedAdditionalServices?.map((additionalService) => {
        const serviceGuest =
          (additionalService.automaticForAdults ? item.occupancy.adults : 0) +
          (additionalService.automaticForChildren ? item.occupancy.children.reduce((quantity, child) => quantity + child.quantity, 0) : 0)

        return (
          <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={0.5} key={additionalService.id}>
            <FontAwesomeIcon icon={faPuzzlePiece} color="var(--QuickSilver)" style={{ width: '13px', height: '13px', marginLeft: '4px', paddingBottom: '4px' }} />
            <Typography variant="caption">
              {`${additionalService.quantity ?? serviceGuest} x `}
              {localizeString(additionalService.name)}{' '}
              {additionalService.pricingType === PricingType.PER_NIGHT || additionalService.pricingType === PricingType.PER_PERSON_PER_NIGHT ? (
                t('pms.booking.reservation_new.cart.additional-services.nights', { count: additionalService.nights })
              ) : (
                <></>
              )}
            </Typography>

            <Typography component="span" variant="caption">
              {t('pms.booking.reservation_new.cart.tot', {
                price: formatCurrency(
                  calculateAdditionalServicePrice({
                    adults: item.occupancy.adults,
                    children: item.occupancy.children,
                    price: additionalService.price,
                    days: item.nights,
                    pricingType: additionalService.pricingType,
                    automaticForAdults: additionalService.automaticForAdults,
                    automaticForChildren: additionalService.automaticForChildren,
                    quantity: additionalService.quantity,
                    childPrices: additionalService.childPrices,
                    priceZero: additionalService.priceZero,
                  }),
                  additionalService.currency,
                ),
              })}
            </Typography>
            <IconButton
              disabled={!props.isEditable}
              size="small"
              sx={{
                margin: '0px',
                padding: '0px',
                minHeight: '15px !important',
                height: '15px !important',
              }}
              onClick={() => {
                const tmpRow = { ...props.item }
                tmpRow.addedAdditionalServices = tmpRow.addedAdditionalServices?.filter((as) => as.id !== additionalService.id)
                if (props.onUpdateRow) props.onUpdateRow(tmpRow)
              }}
            >
              <FontAwesomeIcon icon={faTrash} color={props.isEditable ? 'var(--MaximumRed)' : 'rgb(173,173,173)'} style={{ width: '12px', height: '14px' }} />
            </IconButton>
          </Stack>
        )
      })}
    </>
  )
}

export default ReservationCartServices
